/**
 * FW-OTA-MIGRATOR-BRIDGE — painel do PLANO de atualização em ETAPAS.
 *
 * Chama o preview do backend (`/ota-plan`, NÃO cria comando) para a release
 * selecionada e mostra: ou o caminho seguro em passos (bridge → FS → app final),
 * ou um NO-GO claro quando não há caminho seguro (ex.: device 2.8.9 sem OTA-FS e
 * sem bridge registrada). A EXECUÇÃO é um gate separado — este painel só planeja.
 */
import { useCallback, useEffect, useState } from "react";
import { previewOtaPlan } from "../lib/api.js";

const STEP_LABEL = {
  app_bridge_update: "Firmware bridge/migrador (app OTA — migra a NVS, habilita OTA-FS)",
  wait_heartbeat_bridge: "Aguardar heartbeat com fs_ota=true",
  fs_update: "Enviar Filesystem/LittleFS (OTA-FS)",
  wait_fs_version: "Aguardar o device reportar novo fs_version",
  final_app_update: "Firmware final (app OTA)",
  done: "Concluído",
};

export default function StagedOtaPlanPanel({ deviceId, releaseId, isLab = false }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (signal) => {
    if (!deviceId || !releaseId) { setPlan(null); return; }
    setLoading(true); setError(null);
    try {
      setPlan(await previewOtaPlan(deviceId, releaseId, { is_lab: isLab, signal }));
    } catch (e) {
      if (e?.name !== "AbortError") setError(e?.message || "Falha ao calcular o plano.");
    } finally {
      setLoading(false);
    }
  }, [deviceId, releaseId, isLab]);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  if (!releaseId) return null;
  if (loading) return <div className="text-[12px] text-noc-muted">Calculando plano de atualização…</div>;
  if (error) return (
    <div className="text-[12px] text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">{error}</div>
  );
  if (!plan) return null;

  const stepList = (plan.steps || []).filter((s) => s.step !== "done");

  return (
    <div className="rounded-lg border border-noc-border p-3 space-y-2">
      <div className="text-xs font-semibold text-noc-text flex items-center gap-2">
        🧭 Plano de atualização em etapas
        {plan.allowed
          ? <span className="text-[10px] uppercase text-noc-ok">seguro</span>
          : <span className="text-[10px] uppercase text-noc-err">bloqueado</span>}
      </div>

      {!plan.allowed ? (
        <div className="text-[12px] text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
          <div className="font-semibold">Atualização NÃO liberada ({plan.code})</div>
          <div className="mt-1">{plan.reason}</div>
        </div>
      ) : (
        <>
          <ol className="space-y-1">
            {stepList.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-[12px] text-noc-text">
                <span className="shrink-0 w-5 h-5 rounded-full bg-noc-accent/15 border border-noc-accent/40
                                 text-noc-accent text-[11px] flex items-center justify-center">{i + 1}</span>
                <span>
                  {STEP_LABEL[s.step] || s.step}
                  {s.version && <span className="ml-1 font-mono text-noc-muted">({s.version})</span>}
                  {s.note && <span className="block text-[11px] text-noc-muted">{s.note}</span>}
                </span>
              </li>
            ))}
          </ol>
          <div className="text-[11px] text-noc-muted">
            {plan.reason} — device fw <span className="font-mono">{plan.device_fw || "?"}</span>,
            OTA-FS {plan.fs_supported ? "suportado" : "ausente"}.
          </div>
        </>
      )}
      <div className="text-[10.5px] text-noc-muted border-t border-noc-border pt-1.5">
        Preview apenas — nenhuma etapa é executada aqui. A execução das etapas é um gate separado com GO.
      </div>
    </div>
  );
}
